import { useState } from 'react';
import { DateTime } from 'luxon';
import { useFishDetails } from '../FishDetailsContext';
import { Card, CardContent, CardHeader } from '@Components/Card/index';
import { GuestTabRail } from '@Components/Guest';

/**
 * Section of the Profile tab showing form submissions captured onto the
 * fish's profile at additionalMeta.forms — one card with a tab per form,
 * newest submission first. Renders nothing when no forms have been captured.
 *
 * Each stored response is already { key, label, value } — question labels are
 * resolved when the submission is captured — so this only formats and filters.
 */

// Contact identity is already shown in the profile banner; hiding it here is
// display-only, the stored responses keep everything.
const CONTACT_LABEL_PATTERN =
  /^(first|last|full)?\s*(name|phone|phone number|email|e-mail)$/i;

const isContactResponse = (response) =>
  CONTACT_LABEL_PATTERN.test((response?.label || '').trim());

const ordinal = (day) => {
  if (day >= 11 && day <= 13) return 'th';
  return { 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th';
};

/** e.g. "Feb 1st, 2026 8:21am" */
const formatDate = (value) => {
  if (!value) return null;
  const dt = DateTime.fromJSDate(new Date(value));
  if (!dt.isValid) return null;
  return `${dt.toFormat('MMM')} ${dt.day}${ordinal(dt.day)}, ${dt.toFormat('yyyy h:mm')}${dt.toFormat('a').toLowerCase()}`;
};

export default function CapturedFormsSection() {
  const { capturedForms } = useFishDetails();
  const [selectedFormId, setSelectedFormId] = useState(null);

  const forms = Object.entries(capturedForms || {}).sort(
    ([, a], [, b]) =>
      new Date(b.submittedAt || 0).getTime() -
      new Date(a.submittedAt || 0).getTime(),
  );

  if (!forms.length) {
    return null;
  }

  // Selection survives data refreshes but falls back to the newest form when
  // it points at a form no longer present.
  const activeFormId = forms.some(([formId]) => formId === selectedFormId)
    ? selectedFormId
    : forms[0][0];
  const activeForm = forms.find(([formId]) => formId === activeFormId)[1];

  const submitted = formatDate(activeForm.submittedAt);
  const allResponses = Array.isArray(activeForm.responses)
    ? activeForm.responses
    : [];
  const responses = allResponses.filter(
    (response) => !isContactResponse(response),
  );

  return (
    <Card className="gap-3">
      <CardHeader>
        <h3 className="text-md font-semibold">Profile Enrichment</h3>
      </CardHeader>

      <GuestTabRail
        tabs={forms.map(([formId, form]) => ({
          value: formId,
          label: form.formTitle || formId,
        }))}
        activeTab={activeFormId}
        onTabChange={setSelectedFormId}
        className="scrollbar-dark overflow-x-auto border-b border-aoh-border px-6 dark:border-zinc-700"
      />

      <CardContent>
        {submitted && (
          <p className="mb-2 text-xs text-aoh-muted dark:text-zinc-400">Submitted {submitted}</p>
        )}

        {responses.length ? (
          <dl className="divide-y divide-aoh-border dark:divide-zinc-700/60">
            {responses.map((response, i) => (
              <div
                key={`${response.key || i}`}
                className="grid grid-cols-1 gap-1 py-2 sm:grid-cols-3 sm:gap-4"
              >
                <dt className="text-sm text-aoh-muted dark:text-zinc-400">{response.label}</dt>
                <dd className="text-sm sm:col-span-2">{response.value}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="text-sm text-aoh-muted dark:text-zinc-400">
            {allResponses.length
              ? 'This submission only contained contact information.'
              : 'This submission contained no answers.'}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
